import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, YStack } from 'tamagui';

import ExternalTicketTier from '@/components/ExternalTicketTier';
import PSText from '@/components/ui/PSText';

/**
 * ── /components/external-ticket-tier ─────────────────────────────────────
 *
 * ExternalTicketTier — the "Ticket" section an EXTERNAL event's page shows in
 * place of the tier list (`app/events/[id]/index.tsx`, the `isExternal`
 * block). Two props and no state, so there are no interaction states to
 * preview: what IS worth eyeballing is how the fixed copy holds up against
 * the one thing that varies, the server's `price_from.display` string.
 *
 * The real payload's `display` is whatever the server formatted, which is not
 * always a price: an external event we have no price for comes back `₹0`
 * (see the "Free" / `₹0` entries below), and the "Per ticket" caption under
 * it is hardwired — so those two rows are the ones to look at before deciding
 * whether the caption should learn to disappear.
 *
 * Pressing "Get tickets" here really does open the link (the card owns the
 * `openURL`, see its docstring), so the preview points at a harmless one.
 * ─────────────────────────────────────────────────────────────────────────
 */

const PREVIEW_URL = 'https://www.wikipedia.org/';

const PREVIEW_PRICES: { id: string; label: string; displayPrice: string }[] = [
  {
    id: 'typical',
    label: 'A normal paid external event — the design reference',
    displayPrice: '₹1,417.50',
  },
  {
    id: 'long',
    label:
      'A long display string (paise + thousands separators) — it must not push the caption or wrap oddly',
    displayPrice: '₹12,34,567.89',
  },
  {
    id: 'zero',
    label:
      '₹0 — what the wire sends for an external event whose price we do not know. "Per ticket" still prints; the booking bar HIDES its caption at zero, so these two disagree today',
    displayPrice: '₹0',
  },
  {
    id: 'free',
    label:
      'A non-numeric display string ("Free") — the caption reads oddly too',
    displayPrice: 'Free',
  },
];

export default function ExternalTicketTierPreview() {
  return (
    <>
      <Head>
        <title>ExternalTicketTier · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {PREVIEW_PRICES.map((preview) => (
            <YStack key={preview.id} gap="$xs" alignSelf="stretch">
              <PSText.Body3 color="$textDarkMuted">
                {preview.label}
              </PSText.Body3>
              <ExternalTicketTier
                displayPrice={preview.displayPrice}
                url={PREVIEW_URL}
              />
            </YStack>
          ))}
        </YStack>
      </ScrollView>
    </>
  );
}
